import { Injectable, computed, effect, inject } from '@angular/core';

import { SiteNavigationService } from './site-navigation.service';
import { UtilisateurService }    from 'src/app/services/utilisateur.service';

/**
 * Pont de transition : alimente UtilisateurService depuis la route /new-site.
 *
 * Les composants de l'ancien front lisent tous `utilisateurService.currentSite()`, souvent
 * sans garde (`currentSite().siteId`). Or /new-site passe par SiteNavigationService et ne
 * renseigne jamais UtilisateurService : sur cette route `currentSite()` reste undefined le
 * temps du boot, puis prend la valeur du cookie — qui n'est pas forcément le site de l'URL.
 *
 * Ce service recopie le site de l'URL dans UtilisateurService, et expose `ready` : tant
 * qu'il est faux, aucun composant legacy ne doit être rendu. C'est ce qui évite les crashs
 * sur `undefined.epsg` et les chargements faits avec le mauvais site, sans toucher aux
 * composants eux-mêmes.
 *
 * `setCurrentSite` écrit aussi le cookie `currentSite` — choix assumé : les deux fronts
 * restent alignés sur le même site. Basculer sur `currentSite.set()` pour l'éviter.
 *
 * À supprimer quand le dernier onglet aura été refondu.
 */
@Injectable({ providedIn: 'root' })
export class LegacySiteContextService {

  private readonly siteNav = inject(SiteNavigationService);
  private readonly user    = inject(UtilisateurService);

  readonly ready = computed(() => {
    const siteId = this.siteNav.siteId();
    return !!siteId && this.user.currentSite()?.siteId === siteId;
  });

  constructor() {
    effect(() => {
      const site = this.siteNav.site();
      if (!site || this.user.currentSite()?.siteId === site.siteId) return;

      this.user.setCurrentSite(site);
      if (site.region) {
        this.user.setCurrentRegion(site.region);
      }
    });
  }
}
